/**
 * KnowledgeBasePage — Base de Conhecimento
 * Duas abas internas: Projetos e Operacional.
 * Layout: lista à esquerda + detalhe à direita.
 */

import React, { useState, useEffect, useCallback } from 'react';
import KbApiService from '../services/kbApi';
import { subscribeToProjectComments, subscribeToProjectSteps } from '../services/kbRealtime';
import './KnowledgeBase.css';

// TODO: pegar do contexto de auth
const CURRENT_SELLER = 'diogenes';

const SELLERS_MAP = {
  diogenes: 'Diógenes',
  anna: 'Anna',
  efigenia: 'Efigênia',
};

const STATUS_LABELS = {
  new: 'Novo',
  in_progress: 'Em andamento',
  waiting: 'Aguardando cliente',
  done: 'Concluído',
};

const STATUS_COLORS = {
  new: '#94a3b8',
  in_progress: '#f59e0b',
  waiting: '#3b82f6',
  done: '#22c55e',
};

function KnowledgeBasePage() {
  const [activeTab, setActiveTab] = useState('projects');

  return (
    <div className="kb-page">
      {/* Tabs internas */}
      <div className="kb-page__tabs">
        <button
          className={`kb-page__tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          📂 Projetos
        </button>
        <button
          className={`kb-page__tab ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          📖 Operacional
        </button>
      </div>

      {/* Conteúdo */}
      {activeTab === 'projects' ? <ProjectsView /> : <ArticlesView />}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// PROJETOS
// ═══════════════════════════════════════════════════════════

function ProjectsView() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [loading, setLoading] = useState(false);

  // ─── Carregar projetos ─────────────────────────
  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await KbApiService.getProjects(filterStatus);
      setProjects(data);
    } catch (e) {
      console.error('Erro:', e);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadProjectDetail = useCallback(async (projectId) => {
    try {
      const data = await KbApiService.getProject(projectId);
      setSelectedProject(data);
    } catch (e) {
      console.error('Erro:', e);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  // ─── Realtime para projeto selecionado ─────────
  useEffect(() => {
    if (!selectedProject) return;

    const commentSub = subscribeToProjectComments(selectedProject.id, (comment) => {
      setSelectedProject(prev => ({
        ...prev,
        comments: [...(prev.comments || []), comment],
      }));
    });

    const stepSub = subscribeToProjectSteps(selectedProject.id, (eventType, step) => {
      if (eventType === 'UPDATE') {
        setSelectedProject(prev => ({
          ...prev,
          steps: prev.steps.map(s => s.id === step.id ? step : s),
        }));
      }
    });

    return () => {
      commentSub.unsubscribe();
      stepSub.unsubscribe();
    };
  }, [selectedProject?.id]);

  // ─── Criar projeto ─────────────────────────────
  const handleCreateProject = async (formData) => {
    try {
      const project = await KbApiService.createProject({
        ...formData,
        created_by: CURRENT_SELLER,
      });
      setShowNewForm(false);
      loadProjects();
      loadProjectDetail(project.id);
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  // ─── Marcar etapa como concluída ──────────────
  const handleToggleStep = async (step) => {
    const now = new Date().toISOString();
    const data = step.completed_at
      ? { completed_at: null, completed_by: null }
      : { completed_at: now, completed_by: CURRENT_SELLER };

    try {
      await KbApiService.updateStep(step.id, data);
      loadProjectDetail(selectedProject.id);
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  // ─── Adicionar comentário ─────────────────────
  const handleAddComment = async () => {
    if (!commentInput.trim() || !selectedProject) return;
    try {
      await KbApiService.addComment(selectedProject.id, CURRENT_SELLER, commentInput.trim());
      setCommentInput('');
      // Comentário aparece via Realtime
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  // ─── Atualizar status ─────────────────────────
  const handleStatusChange = async (newStatus) => {
    try {
      await KbApiService.updateProject(selectedProject.id, { status: newStatus });
      loadProjectDetail(selectedProject.id);
      loadProjects();
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  // ─── Filtrar lista ────────────────────────────
  const filteredProjects = projects.filter(p => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return p.title.toLowerCase().includes(term) ||
        (p.tags || []).some(t => t.toLowerCase().includes(term));
    }
    return true;
  });

  return (
    <div className="kb-content">
      {/* Lista de projetos */}
      <div className="kb-content__list">
        <div className="kb-content__list-actions">
          <input
            type="text"
            className="kb-content__search"
            placeholder="🔍 Buscar projetos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button className="kb-content__new-btn" onClick={() => setShowNewForm(true)}>
            + Novo
          </button>
        </div>

        {/* Filtros de status */}
        <div className="kb-content__filters">
          <button
            className={`kb-content__filter ${!filterStatus ? 'active' : ''}`}
            onClick={() => setFilterStatus(null)}
          >Todos</button>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              className={`kb-content__filter ${filterStatus === key ? 'active' : ''}`}
              onClick={() => setFilterStatus(key)}
            >
              <span className="status-dot" style={{ background: STATUS_COLORS[key] }} />
              {label}
            </button>
          ))}
        </div>

        {loading && <div className="kb-content__loading">Carregando...</div>}

        {filteredProjects.map(project => (
          <div
            key={project.id}
            className={`kb-project-item ${selectedProject?.id === project.id ? 'active' : ''}`}
            onClick={() => loadProjectDetail(project.id)}
          >
            <div className="kb-project-item__title">{project.title}</div>
            <div className="kb-project-item__meta">
              <span className="status-dot" style={{ background: STATUS_COLORS[project.status] }} />
              <span>{STATUS_LABELS[project.status] || project.status}</span>
              {project.assigned_to && (
                <span className="kb-project-item__assigned">
                  · {SELLERS_MAP[project.assigned_to] || project.assigned_to}
                </span>
              )}
            </div>
            {project.tags && project.tags.length > 0 && (
              <div className="kb-project-item__tags">
                {project.tags.map((tag, i) => (
                  <span key={i} className="kb-tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Detalhe do projeto */}
      <div className="kb-content__detail">
        {showNewForm ? (
          <NewProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowNewForm(false)}
          />
        ) : selectedProject ? (
          <ProjectDetail
            project={selectedProject}
            onToggleStep={handleToggleStep}
            onStatusChange={handleStatusChange}
            commentInput={commentInput}
            onCommentChange={setCommentInput}
            onAddComment={handleAddComment}
          />
        ) : (
          <div className="kb-content__empty">Selecione um projeto para ver os detalhes</div>
        )}
      </div>
    </div>
  );
}


// ─── Detalhe do Projeto ──────────────────────────

function ProjectDetail({ project, onToggleStep, onStatusChange, commentInput, onCommentChange, onAddComment }) {
  return (
    <div className="kb-project-detail">
      {/* Header */}
      <div className="kb-project-detail__header">
        <h2>{project.title}</h2>
        <div className="kb-project-detail__meta">
          <select
            className="kb-project-detail__status-select"
            value={project.status}
            onChange={e => onStatusChange(e.target.value)}
            style={{ borderColor: STATUS_COLORS[project.status] }}
          >
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          {project.assigned_to && (
            <span className="kb-tag blue">{SELLERS_MAP[project.assigned_to] || project.assigned_to}</span>
          )}
          {project.template_type && (
            <span className="kb-tag purple">{project.template_type}</span>
          )}
        </div>
      </div>

      {/* Etapas */}
      <div className="kb-project-detail__section">
        <h4>ETAPAS</h4>
        {(project.steps || []).map(step => (
          <div key={step.id} className={`kb-step ${step.completed_at ? 'done' : ''}`}>
            <button
              className="kb-step__check"
              onClick={() => onToggleStep(step)}
            >
              {step.completed_at ? '✓' : step.position}
            </button>
            <div className="kb-step__info">
              <div className="kb-step__title">{step.title}</div>
              {step.notes && <div className="kb-step__notes">{step.notes}</div>}
              {step.completed_by && (
                <div className="kb-step__completed">
                  {SELLERS_MAP[step.completed_by] || step.completed_by} —{' '}
                  {new Date(step.completed_at).toLocaleDateString('pt-BR')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Comentários */}
      <div className="kb-project-detail__section">
        <h4>COMENTÁRIOS</h4>
        {(project.comments || []).map(comment => (
          <div key={comment.id} className="kb-comment">
            <div className="kb-comment__header">
              <strong>{SELLERS_MAP[comment.author_id] || comment.author_id}</strong>
              <span className="kb-comment__time">
                {new Date(comment.created_at).toLocaleString('pt-BR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </span>
            </div>
            <div className="kb-comment__content">{comment.content}</div>
          </div>
        ))}

        <div className="kb-comment__input-area">
          <input
            type="text"
            className="kb-comment__input"
            placeholder="Adicionar comentário..."
            value={commentInput}
            onChange={e => onCommentChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onAddComment()}
          />
          <button className="kb-comment__send" onClick={onAddComment} disabled={!commentInput.trim()}>
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Form Novo Projeto ───────────────────────────

function NewProjectForm({ onSubmit, onCancel }) {
  const [title, setTitle] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [tags, setTags] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      template_type: templateType || null,
      assigned_to: assignedTo || null,
      tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
  };

  return (
    <form className="kb-new-form" onSubmit={handleSubmit}>
      <h3>Novo Projeto</h3>

      <label>Título *</label>
      <input type="text" value={title} onChange={e => setTitle(e.target.value)}
        placeholder="Ex: Visto B1/B2 — João Silva" required />

      <label>Tipo</label>
      <select value={templateType} onChange={e => setTemplateType(e.target.value)}>
        <option value="">Selecionar...</option>
        <option value="visto_americano">Visto Americano</option>
        <option value="passaporte">Passaporte</option>
        <option value="visto_schengen">Visto Schengen</option>
        <option value="visto_canadense">Visto Canadense</option>
        <option value="pacote_especial">Pacote Especial</option>
        <option value="outro">Outro</option>
      </select>

      <label>Responsável</label>
      <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
        <option value="">Selecionar...</option>
        {Object.entries(SELLERS_MAP).map(([id, name]) => (
          <option key={id} value={id}>{name}</option>
        ))}
      </select>

      <label>Tags (separadas por vírgula)</label>
      <input type="text" value={tags} onChange={e => setTags(e.target.value)}
        placeholder="Ex: urgente, família, primeiro visto" />

      <div className="kb-new-form__actions">
        <button type="button" className="kb-new-form__cancel" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="kb-new-form__submit">Criar Projeto</button>
      </div>
    </form>
  );
}


// ═══════════════════════════════════════════════════════════
// OPERACIONAL (Artigos / Wiki)
// ═══════════════════════════════════════════════════════════

function ArticlesView() {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  // ─── Carregar artigos ─────────────────────────
  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await KbApiService.getArticles(filterCategory);
      setArticles(data);
    } catch (e) {
      console.error('Erro:', e);
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await KbApiService.getCategories();
      setCategories(cats);
    } catch (e) {
      console.error('Erro:', e);
    }
  }, []);

  useEffect(() => { loadArticles(); loadCategories(); }, [loadArticles, loadCategories]);

  // ─── Busca ─────────────────────────────────────
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadArticles();
      return;
    }
    setLoading(true);
    try {
      const results = await KbApiService.searchArticles(searchTerm);
      setArticles(results);
    } catch (e) {
      console.error('Erro:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // ─── Selecionar artigo ─────────────────────────
  const handleSelectArticle = async (articleId) => {
    try {
      const article = await KbApiService.getArticle(articleId);
      setSelectedArticle(article);
      setShowEditor(false);
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  // ─── Criar / Editar artigo ────────────────────
  const handleSaveArticle = async (formData) => {
    try {
      if (editingArticle) {
        await KbApiService.updateArticle(editingArticle.id, {
          ...formData,
          updated_by: CURRENT_SELLER,
        });
      } else {
        await KbApiService.createArticle({
          ...formData,
          author_id: CURRENT_SELLER,
        });
      }
      setShowEditor(false);
      setEditingArticle(null);
      loadArticles();
      loadCategories();
    } catch (e) {
      console.error('Erro:', e);
    }
  };

  const handleEditArticle = () => {
    setEditingArticle(selectedArticle);
    setShowEditor(true);
  };

  const handleNewArticle = () => {
    setEditingArticle(null);
    setShowEditor(true);
  };

  return (
    <div className="kb-content">
      {/* Lista de artigos */}
      <div className="kb-content__list">
        <div className="kb-content__list-actions">
          <input
            type="text"
            className="kb-content__search"
            placeholder="🔍 Buscar artigos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button className="kb-content__new-btn" onClick={handleNewArticle}>
            + Novo
          </button>
        </div>

        {/* Filtros de categoria */}
        <div className="kb-content__filters">
          <button
            className={`kb-content__filter ${!filterCategory ? 'active' : ''}`}
            onClick={() => setFilterCategory(null)}
          >Todos</button>
          {categories.map(cat => (
            <button
              key={cat}
              className={`kb-content__filter ${filterCategory === cat ? 'active' : ''}`}
              onClick={() => setFilterCategory(cat)}
            >{cat}</button>
          ))}
        </div>

        {loading && <div className="kb-content__loading">Carregando...</div>}

        {articles.map(article => (
          <div
            key={article.id}
            className={`kb-article-item ${selectedArticle?.id === article.id ? 'active' : ''}`}
            onClick={() => handleSelectArticle(article.id)}
          >
            <div className="kb-article-item__title">{article.title}</div>
            <div className="kb-article-item__meta">
              <span className="kb-tag">{article.category}</span>
              {(article.tags || []).slice(0, 3).map((tag, i) => (
                <span key={i} className="kb-tag small">{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Detalhe / Editor */}
      <div className="kb-content__detail">
        {showEditor ? (
          <ArticleEditor
            article={editingArticle}
            categories={categories}
            onSave={handleSaveArticle}
            onCancel={() => { setShowEditor(false); setEditingArticle(null); }}
          />
        ) : selectedArticle ? (
          <ArticleDetail article={selectedArticle} onEdit={handleEditArticle} />
        ) : (
          <div className="kb-content__empty">Selecione um artigo ou crie um novo</div>
        )}
      </div>
    </div>
  );
}


// ─── Detalhe do Artigo ───────────────────────────

function ArticleDetail({ article, onEdit }) {
  return (
    <div className="kb-article-detail">
      <div className="kb-article-detail__header">
        <h2>{article.title}</h2>
        <button className="kb-article-detail__edit-btn" onClick={onEdit}>✏️ Editar</button>
      </div>
      <div className="kb-article-detail__meta">
        <span className="kb-tag">{article.category}</span>
        {(article.tags || []).map((tag, i) => (
          <span key={i} className="kb-tag small">{tag}</span>
        ))}
        <span className="kb-article-detail__date">
          Atualizado em {new Date(article.updated_at).toLocaleDateString('pt-BR')}
        </span>
      </div>
      <div className="kb-article-detail__content">
        {/* Renderiza o conteúdo como parágrafos simples (sem markdown parser por enquanto) */}
        {article.content.split('\n').map((line, i) => (
          line.trim() ? <p key={i}>{line}</p> : <br key={i} />
        ))}
      </div>
    </div>
  );
}


// ─── Editor de Artigo ────────────────────────────

function ArticleEditor({ article, categories, onSave, onCancel }) {
  const [title, setTitle] = useState(article?.title || '');
  const [content, setContent] = useState(article?.content || '');
  const [category, setCategory] = useState(article?.category || '');
  const [newCategory, setNewCategory] = useState('');
  const [tags, setTags] = useState((article?.tags || []).join(', '));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    const finalCategory = newCategory.trim() || category;
    if (!finalCategory) return;

    onSave({
      title: title.trim(),
      content: content.trim(),
      category: finalCategory,
      tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
  };

  return (
    <form className="kb-article-editor" onSubmit={handleSubmit}>
      <h3>{article ? 'Editar Artigo' : 'Novo Artigo'}</h3>

      <label>Título *</label>
      <input type="text" value={title} onChange={e => setTitle(e.target.value)}
        placeholder="Ex: Como tirar visto americano B1/B2" required />

      <label>Categoria *</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={category} onChange={e => { setCategory(e.target.value); setNewCategory(''); }}
          style={{ flex: 1 }}>
          <option value="">Selecionar...</option>
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <input type="text" value={newCategory} onChange={e => { setNewCategory(e.target.value); setCategory(''); }}
          placeholder="ou criar nova..." style={{ flex: 1 }} />
      </div>

      <label>Tags (separadas por vírgula)</label>
      <input type="text" value={tags} onChange={e => setTags(e.target.value)}
        placeholder="Ex: visto, americano, b1b2, entrevista" />

      <label>Conteúdo *</label>
      <textarea value={content} onChange={e => setContent(e.target.value)}
        placeholder="Escreva o conteúdo do artigo..." rows={15} required />

      <div className="kb-new-form__actions">
        <button type="button" className="kb-new-form__cancel" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="kb-new-form__submit">
          {article ? 'Salvar' : 'Criar Artigo'}
        </button>
      </div>
    </form>
  );
}


export default KnowledgeBasePage;
